/**
 * The coach's tutorial progress over HTTP — `/api/tutorial`: which steps they
 * have confirmed. Kept with the account, so the tutorial is not shown again on
 * a new device.
 */

import type { TutorialStore } from './types.ts';

export class HttpTutorialStore implements TutorialStore {
  readonly #getToken: () => Promise<string | null>;
  readonly #fetch: typeof fetch;

  constructor(getToken: () => Promise<string | null>, fetchImpl: typeof fetch = (...args) => fetch(...args)) {
    this.#getToken = getToken;
    this.#fetch = fetchImpl;
  }

  async confirmed(): Promise<ReadonlySet<string>> {
    const response = await this.#request('GET', '/api/tutorial');
    if (!response.ok) throw new Error(`Could not load the tutorial (${response.status})`);
    const body = (await response.json()) as { confirmed?: unknown };
    return new Set(Array.isArray(body.confirmed) ? body.confirmed.filter((id): id is string => typeof id === 'string') : []);
  }

  async confirm(stepId: string): Promise<void> {
    await this.#request('PUT', `/api/tutorial/steps/${encodeURIComponent(stepId)}`);
  }

  async unconfirm(stepId: string): Promise<void> {
    await this.#request('DELETE', `/api/tutorial/steps/${encodeURIComponent(stepId)}`);
  }

  async #request(method: 'GET' | 'PUT' | 'DELETE', path: string): Promise<Response> {
    const token = await this.#getToken();
    if (!token) throw new Error('Not signed in');
    return this.#fetch(path, { method, headers: { authorization: `Bearer ${token}` } });
  }
}

/** For tests and for anything that must not hit the network. */
export class MemoryTutorialStore implements TutorialStore {
  readonly done = new Set<string>();

  async confirmed(): Promise<ReadonlySet<string>> {
    return new Set(this.done);
  }

  async confirm(stepId: string): Promise<void> {
    this.done.add(stepId);
  }

  async unconfirm(stepId: string): Promise<void> {
    this.done.delete(stepId);
  }
}
